<template>
  <div id="app">
    <!-- 导航栏 -->
    <nav class="navbar" :class="{ 'scrolled': isScrolled }">
      <div class="container">
        <!-- 左侧Logo -->
        <div class="nav-logo">
          <a href="#home" class="logo-link">
            <span class="logo-text">Herlinde</span>
            <span class="logo-subtext">{{ $t('nav_cashier') }}</span>
          </a>
        </div>

        <!-- 右侧操作区 -->
        <div class="nav-actions">
          <!-- 语言切换 -->
          <div class="language-switch">
            <ChangeLanguage></ChangeLanguage>
          </div>
        </div>
      </div>
    </nav>

    <!-- 英雄区域 -->
    <section id="home" class="hero">
      <canvas id="smoke-canvas"></canvas>
      <div class="hero-content">
        <div class="payment-title">{{ $t('payment_title') }}</div>
        <div class="payment-container">
          <!-- 金额输入 -->
          <div class="amount-section">
            <label for="amount">{{ $t('payment_amount_label') }}</label>
            <div class="amount-input-wrapper">
              <span class="currency-symbol">HK$</span>
              <input type="number" id="amount" v-model="paymentAmount" placeholder="0.00" step="0.01" min="0">
            </div>
          </div>

          <!-- 支付方式选择 -->
          <div class="payment-methods">
            <h3>{{ $t('payment_method_title') }}</h3>
            <div class="payment-options">
              <div class="payment-option" :class="{ active: selectedPayment === 'wechat' }" @click="selectedPayment = 'wechat'">
                <img src="./assets/img/wxpay.png" :alt="$t('payment_method_wechat')" class="payment-image">
                <span>{{ $t('payment_method_wechat') }}</span>
                <div class="radio-button" :class="{ checked: selectedPayment === 'wechat' }"></div>
              </div>
              <div class="payment-option" :class="{ active: selectedPayment === 'alipay' }" @click="selectedPayment = 'alipay'">
                <img src="./assets/img/alipay.png" :alt="$t('payment_method_alipay')" class="payment-image">
                <span>{{ $t('payment_method_alipay') }}</span>
                <div class="radio-button" :class="{ checked: selectedPayment === 'alipay' }"></div>
              </div>
              <div class="payment-option" :class="{ active: selectedPayment === 'unionpay' }" @click="selectedPayment = 'unionpay'">
                <img src="./assets/img/unionpay.png" :alt="$t('payment_method_unionpay')" class="payment-image">
                <span>{{ $t('payment_method_unionpay') }}</span>
                <div class="radio-button" :class="{ checked: selectedPayment === 'unionpay' }"></div>
              </div>
            </div>
          </div>

          <!-- 支付按钮 -->
          <button class="btn primary pay-button" @click="handlePayment" :disabled="!paymentAmount || Number(paymentAmount) <= 0 || isLoading">
            <span v-if="!isLoading">{{ $t('payment_button', { amount: Number(paymentAmount).toFixed(2) }) }}</span>
            <span v-else class="loading-spinner">
              <span class="spinner"></span> {{ $t('payment_loading') }}
            </span>
          </button>
        </div>
      </div>
      <div class="hero-image">
        <div class="hero-bg"></div>
      </div>
    </section>

    <!-- 支付调起页面 -->
    <div class="payment-modal-overlay" v-if="showPaymentModal">
      <div class="payment-modal">
        <div class="payment-modal-header">
          <div class="payment-modal-logo">
            <img v-if="selectedPayment === 'wechat'" src="./assets/img/wxpay.png" alt="微信支付" class="payment-modal-icon">
            <img v-else-if="selectedPayment === 'alipay'" src="./assets/img/alipay.png" alt="支付宝" class="payment-modal-icon">
            <img v-else src="./assets/img/unionpay.png" alt="银联" class="payment-modal-icon">
            <span class="payment-modal-title">{{ getPaymentName(selectedPayment) }}</span>
          </div>
        </div>
        <div class="payment-modal-body">
          <div class="payment-amount-display">
            <span class="payment-amount-label">{{ $t('payment_amount') }}</span>
            <span class="payment-amount-value">HK$ {{ Number(paymentAmount).toFixed(2) }}</span>
          </div>

          <!-- 模拟密码输入界面 -->
          <div class="payment-password-section">
            <h4 class="password-title">{{ $t('payment_password_title') }}</h4>
            <div class="password-input-container">
              <input type="password" v-model="paymentPassword" class="password-input" :placeholder="$t('payment_password_placeholder')">
            </div>
            <p class="password-hint">{{ $t('payment_password_hint', { paymentMethod: getPaymentName(selectedPayment) }) }}</p>
          </div>

          <div class="payment-actions">
            <button class="payment-action-button cancel" @click="showPaymentModal = false">{{ $t('payment_cancel') }}</button>
            <button class="payment-action-button confirm" @click="confirmPayment">{{ $t('payment_confirm') }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 银联二维码支付模态框 -->
    <div class="unionpay-modal-overlay" v-if="showUnionpayModal">
      <div class="unionpay-modal">
        <div class="unionpay-modal-header">
          <div class="unionpay-modal-logo">
            <img src="./assets/img/unionpay.png" alt="银联" class="unionpay-modal-icon">
            <span class="unionpay-modal-title">{{ $t('unionpay_scan_title') }}</span>
          </div>
        </div>
        <div class="unionpay-modal-body">
          <div class="payment-amount-display">
            <span class="payment-amount-label">{{ $t('payment_amount') }}</span>
            <span class="payment-amount-value">HK$ {{ Number(paymentAmount).toFixed(2) }}</span>
          </div>

          <!-- 二维码显示区域 -->
          <div class="qrcode-section">
            <div class="qrcode-container" :class="{ 'mobile-long-press': isMobileDevice }">
              <img :src="unionpayQrCode" alt="支付二维码" class="qrcode-image" v-if="unionpayQrCode">
              <div class="mobile-hint" v-if="isMobileDevice">{{ $t('unionpay_mobile_long_press') || '长按保存图片' }}</div>
            </div>
            <p class="qrcode-hint">{{ $t('unionpay_scan_hint') }}</p>
          </div>

          <div class="unionpay-actions">
            <button class="unionpay-action-button cancel" @click="closeUnionpayModal" @touchstart="">{{ $t('payment_cancel') }}</button>
            <button class="unionpay-action-button download" @click="downloadUnionpayQrCode" @touchstart="">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              {{ $t('unionpay_download_button') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 支付成功提示 -->
    <div class="success-modal-overlay" v-if="showSuccessModal">
      <div class="success-modal">
        <div class="success-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M8 14L11 17L16 12"></path>
          </svg>
        </div>
        <h3 class="success-title">{{ $t('payment_success') }}</h3>
        <div class="success-details">
          <div class="success-detail-item">
            <span class="success-detail-label">{{ $t('payment_amount') }}</span>
            <span class="success-detail-value">HK$ {{ paymentResult ? paymentResult.amount.toFixed(2) : '0.00' }}</span>
          </div>
          <div class="success-detail-item">
            <span class="success-detail-label">{{ $t('payment_method') }}</span>
            <span class="success-detail-value">{{ paymentResult ? getPaymentName(paymentResult.paymentMethod) : '' }}</span>
          </div>
          <div class="success-detail-item">
            <span class="success-detail-label">{{ $t('payment_transaction_id') }}</span>
            <span class="success-detail-value">{{ paymentResult ? paymentResult.transactionId : '' }}</span>
          </div>
          <div class="success-detail-item">
            <span class="success-detail-label">{{ $t('payment_time') }}</span>
            <span class="success-detail-value">{{ paymentResult ? paymentResult.time : '' }}</span>
          </div>
        </div>
        <div class="success-modal-footer">
          <button class="success-modal-button" @click="showSuccessModal = false">{{ $t('payment_ok') }}</button>
        </div>
      </div>
    </div>

    <!-- WhatsApp悬浮按钮 -->
    <a href="https://wa.me/004917641138247" target="_blank" class="whatsapp-float">
      <img src="./assets/img/WhatsApp.png" alt="WhatsApp" class="whatsapp-icon">
    </a>

  </div>
</template>

<script>
import ChangeLanguage from './i18n/change-language.vue';
import paymentApi from '@/api/payment';

export default {
  name: 'App',
  components: {
    ChangeLanguage,
  },

  data() {
    return {
      isMenuOpen: false,
      showProductCenter: false,
      isScrolled: false, // 导航栏滚动状态
      // 支付相关数据
      paymentAmount: '',
      selectedPayment: 'unionpay', // 默认选中支付宝支付
      isLoading: false, // 支付加载状态
      paymentResult: null, // 支付结果
      showPaymentModal: false, // 支付调起页面
      showSuccessModal: false, // 支付成功提示
      paymentPassword: '', // 支付密码
      alipayFormHtml: '', // 支付宝表单HTML
      unionpayQrCode: '', // 银联二维码图片URL
      showUnionpayModal: false, // 银联支付模态框
      isMobileDevice: false // 是否为移动设备

    };
  },
    mounted() {
    this.initNavbar();
    this.initScrollEffects();
    this.initSmokeParticles();
    
    // 检测是否为移动设备
    this.isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // 添加滚动事件监听，用于所有滚动效果
    window.addEventListener('scroll', this.handleScroll, { passive: false });

    // 添加导航栏滚动监听
    window.addEventListener('scroll', this.handleNavbarScroll);
  },
  beforeDestroy() {
    // 清理事件监听器和动画
    const canvas = document.getElementById('smoke-canvas');
    if (canvas) {
      canvas.removeEventListener('mousemove', this.createParticle);
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    // 清理自动播放定时器
    this.stopAutoPlay();

    // 移除事件监听
    window.removeEventListener('scroll', this.handleScroll, { passive: false });
    window.removeEventListener('scroll', this.handleNavbarScroll);
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      const navLinks = document.querySelector('.nav-links');
      const hamburger = document.querySelector('.hamburger');
      navLinks.classList.toggle('active');
      hamburger.classList.toggle('active');
    },
    // 处理导航栏滚动效果
    handleNavbarScroll() {
      this.isScrolled = window.scrollY > 50;
    },

    // 处理支付
    async handlePayment() {
      const amount = Number(this.paymentAmount);
      if (!amount || amount <= 0) {
        alert(this.$t('payment_amount_error'));
        return;
      }

      // 显示加载状态
      this.isLoading = true;
      this.paymentResult = null;
      this.alipayFormHtml = '';

      try {
        // 根据支付方式执行不同的逻辑
        if (this.selectedPayment === 'alipay') {
          // 支付宝支付
          const response = await paymentApi.alipayH5({
            amount: amount.toFixed(2)
          });

          if (response.code === 200 && response.data && response.data.payInfo) {
            // 直接生成并提交支付宝表单
            this.submitAlipayForm(response.data.payInfo);
          } else {
            this.isLoading = false;
            alert(response.message || '支付初始化失败');
          }
        } else if (this.selectedPayment === 'wechat') {
          // 微信支付（暂未对接）
          this.isLoading = false;
          alert('请选择其他支付方式');
        } else if (this.selectedPayment === 'unionpay') {
          // 银联支付
          const response = await paymentApi.unionpayScan({
            amount: amount.toFixed(2)
          });

          if (response.code === 200 && response.data && response.data.payUrl) {
            // 使用payUrl生成二维码图片
            this.unionpayQrCode = this.generateQrCodeImage(response.data.payUrl);
            this.showUnionpayModal = true;
            this.isLoading = false;
          } else {
            this.isLoading = false;
            alert(response.message || '支付初始化失败');
          }
        } else {
          // 未知支付方式
          this.isLoading = false;
          alert('不支持的支付方式');
        }
      } catch (error) {
        this.isLoading = false;
        alert(this.$t('payment_request_error') || '支付请求失败，请稍后重试');
      }
    },
    // 生成并提交支付宝表单
    submitAlipayForm(payInfoStr) {
      try {
        // 解析payInfo JSON字符串
        const payInfo = typeof payInfoStr === 'string'
          ? JSON.parse(payInfoStr)
          : payInfoStr;

        // 获取action URL和method
        const actionUrl = payInfo.action || 'https://api-sea-global.alipayplus.com/gateway.do';
        const method = payInfo.method || 'post';

        // 创建form表单
        const form = document.createElement('form');
        form.id = 'alipaysubmit';
        form.name = 'alipaysubmit';
        form.method = method;
        form.action = actionUrl;
        form.style.display = 'none';

        // 遍历payInfo中的所有字段，创建hidden input
        Object.keys(payInfo).forEach(key => {
          // 跳过action和method字段，因为它们已经用作form的action和method属性
          if (key !== 'action' && key !== 'method') {
            const input = document.createElement('input');
            input.type = 'hidden';
            input. name = key;
            input.value = payInfo[key];
            form.appendChild(input);
          }
        });

        // 打印拼接好的form
        // eslint-disable-next-line no-console
        console.log('=== 支付宝Form表单信息 ===');
        // eslint-disable-next-line no-console
        console.log('Action URL:', actionUrl);
        // eslint-disable-next-line no-console
        console.log('Form HTML:', form.outerHTML);
        // eslint-disable-next-line no-console
        console.log('PayInfo数据:', payInfo);
        // eslint-disable-next-line no-console
        console.log('========================');

        // 将表单添加到body
        document.body.appendChild(form);

        // 提交表单
        form.submit();
      } catch (error) {
        this.isLoading = false;
        alert('支付表单生成失败，请重试');
      }
    },
    // 下载银联二维码
    downloadUnionpayQrCode() {
      // eslint-disable-next-line no-console
      console.log('点击下载按钮', this.unionpayQrCode);
      
      if (!this.unionpayQrCode) {
        alert(this.$t('unionpay_download_failed'));
        return;
      }

      // 检测是否为移动设备
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // eslint-disable-next-line no-console
      console.log('是否移动设备:', isMobile, 'UserAgent:', navigator.userAgent);
      
      if (isMobile) {
        // 移动端：长按保存提示
        // eslint-disable-next-line no-console
        console.log('显示移动端提示');
        this.showMobileSaveHint();
      } else {
        // PC端：尝试下载
        // eslint-disable-next-line no-console
        console.log('显示PC端提示');
        this.downloadForDesktop();
      }
    },
    
    // 移动端保存提示
    showMobileSaveHint() {
      try {
        // eslint-disable-next-line no-console
        console.log('开始创建移动端提示框');
        
        // 创建一个友好的提示框
        const hintOverlay = document.createElement('div');
        hintOverlay.className = 'mobile-save-hint-overlay';
        hintOverlay.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.3s ease;
          padding: 20px;
        `;
        
        hintOverlay.innerHTML = `
          <div class="mobile-save-hint" style="
            background: white;
            border-radius: 16px;
            width: 100%;
            max-width: 320px;
            padding: 30px 20px;
            text-align: center;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
            animation: slideUp 0.3s ease;
          ">
            <div class="hint-icon" style="margin-bottom: 15px;">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#e60012" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 16v-4"></path>
                <path d="M12 8h.01"></path>
              </svg>
            </div>
            <h3 style="font-size: 18px; color: #333; margin: 0 0 15px 0; font-weight: 600;">${this.$t('unionpay_scan_title')}</h3>
            <p class="hint-text" style="font-size: 14px; color: #666; line-height: 1.6; margin: 0 0 20px 0;">${this.$t('unionpay_mobile_save_hint') || '长按图片，选择"保存图片"即可'}</p>
            <button class="hint-close-btn" style="
              width: 100%;
              padding: 12px;
              background: #e60012;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 500;
              cursor: pointer;
            ">${this.$t('payment_ok') || '我知道了'}</button>
          </div>
        `;
        
        document.body.appendChild(hintOverlay);
        
        // eslint-disable-next-line no-console
        console.log('提示框已添加到DOM');
        
        // 关闭按钮事件
        const closeBtn = hintOverlay.querySelector('.hint-close-btn');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (document.body.contains(hintOverlay)) {
              document.body.removeChild(hintOverlay);
            }
          });
        }
        
        // 点击遮罩关闭
        hintOverlay.addEventListener('click', (e) => {
          if (e.target === hintOverlay && document.body.contains(hintOverlay)) {
            document.body.removeChild(hintOverlay);
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('显示移动端提示失败:', error);
        alert(this.$t('unionpay_mobile_save_hint') || '长按图片，选择"保存图片"即可');
      }
    },
    
    // PC端下载
    downloadForDesktop() {
      try {
        // 方法1: 直接打开图片新窗口（最可靠）
        const newWindow = window.open(this.unionpayQrCode, '_blank');
        
        if (newWindow) {
          // 如果成功打开新窗口，显示提示
          setTimeout(() => {
            this.showDesktopSaveHint();
          }, 500);
        } else {
          // 如果弹窗被阻止，使用方法2
          this.fallbackDownloadMethod();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('下载失败:', error);
        this.fallbackDownloadMethod();
      }
    },
    
    // 备用下载方法
    fallbackDownloadMethod() {
      // 方法2: 创建带下载属性的链接
      const link = document.createElement('a');
      link.href = this.unionpayQrCode;
      link.download = `unionpay_qrcode_${Date.now()}.png`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 显示保存提示
      this.showDesktopSaveHint();
    },
    
    // PC端保存提示
    showDesktopSaveHint() {
      const hintOverlay = document.createElement('div');
      hintOverlay.className = 'desktop-save-hint-overlay';
      hintOverlay.innerHTML = `
        <div class="desktop-save-hint">
          <div class="hint-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </div>
          <h3>${this.$t('unionpay_download_success') || '二维码已准备就绪'}</h3>
          <p class="hint-text">${this.$t('unionpay_desktop_save_hint') || '请在新窗口中右键保存图片'}</p>
          <button class="hint-close-btn">${this.$t('payment_ok') || '确定'}</button>
        </div>
      `;
      
      document.body.appendChild(hintOverlay);
      
      hintOverlay.querySelector('.hint-close-btn').addEventListener('click', () => {
        document.body.removeChild(hintOverlay);
      });
      
      hintOverlay.addEventListener('click', (e) => {
        if (e.target === hintOverlay) {
          document.body.removeChild(hintOverlay);
        }
      });
    },
    // 关闭银联支付模态框
    closeUnionpayModal() {
      this.showUnionpayModal = false;
      this.unionpayQrCode = '';
    },
    // 生成二维码图片（使用第三方API）
    generateQrCodeImage(text) {
      // 使用qrserver API生成二维码
      const size = 280;
      return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}`;
    },
    // 获取支付方式名称
    getPaymentName(paymentType) {
      const paymentNames = {
        wechat: this.$t('payment_method_wechat'),
        alipay: this.$t('payment_method_alipay'),
        unionpay: this.$t('payment_method_unionpay')
      };
      return paymentNames[paymentType] || paymentType;
    },
    // 滚动到指定区域
    scrollToSection(sectionId) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    // 初始化滚动效果
    initScrollEffects() {
      // 初始化时检查是否需要执行动画
      this.handleScroll();
    },

    // 处理滚动事件
    handleScroll() {
      // 处理英雄区域动画
      const heroSection = document.querySelector('.hero');
      if (heroSection) {
        const heroContent = heroSection.querySelector('.hero-content');
        const rect = heroSection.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;

        if (isVisible) {
          heroContent.classList.add('animate');
        } else {
          heroContent.classList.remove('animate');
        }
      }

      // 处理 BENEFITS 模块的滚动效果
      // 处理features区域的滚动类和缩放效果
      const featuresSection = document.querySelector('.features-transform');
      if (featuresSection) {
        // 到达features锚点的条件：区域顶部进入视口顶部
        const rect = featuresSection.getBoundingClientRect();
        const hasReachedFeaturesAnchor = rect.top <= 0;

        // 添加/移除滚动类
        featuresSection.classList.toggle('scrolled', hasReachedFeaturesAnchor);
      }
    },

    // 移除复杂的wheel事件处理，使用更简单的scroll事件实现





    // 移除自动滚动相关方法
    handleNavLinkClick(event) {
      // 检查点击的是否是导航链接
      if (event.target.tagName === 'A') {
        event.preventDefault(); // 阻止默认的跳转行为

        // 获取锚点链接的href属性
        const href = event.target.getAttribute('href');

        // 如果是锚点链接
        if (href.startsWith('#')) {
          // 提取目标锚点的ID
          const targetId = href.slice(1);

          // 根据ID获取目标元素
          const targetElement = document.getElementById(targetId);

          // 如果找到目标元素，则平滑滚动到该元素
          if (targetElement) {
            // 重置动画状态，使动画可以重新播放
            this.resetAnimations();

            // 平滑滚动到目标元素
            targetElement.scrollIntoView({
              behavior: 'smooth', // 平滑滚动
              block: 'start' // 滚动到元素的顶部
            });

            // 在滚动完成后重新触发动画
            setTimeout(() => {
              this.handleScroll();
            }, 1000); // 等待滚动完成后再触发动画
          }
        }

        // 如果菜单是打开状态，则关闭菜单
        if (this.isMenuOpen) {
          this.toggleMenu();
        }
      }
    },

    // 重置动画状态
    resetAnimations() {
      // 重置英雄区域动画
      const heroContent = document.querySelector('.hero-content');
      if (heroContent) {
        heroContent.classList.remove('animate');
        // 强制重排以确保动画可以重新触发
        void heroContent.offsetWidth;
      }

      // 重置跑马灯文本动画
      const heroText = document.querySelector('.hero-text');
      if (heroText) {
        heroText.style.animation = 'none';
        // 强制重排以确保动画可以重新触发
        void heroText.offsetWidth;
      }
    },
    initNavbar() {
      // 移除重复的事件监听器，使用Vue的@click事件绑定即可
    },
    // 初始化烟雾粒子效果
    initSmokeParticles() {
      // 为home区域初始化烟雾特效
      this.initCanvasEffect('smoke-canvas', '.hero');
      // 监听窗口大小变化，调整canvas大小
      window.addEventListener('resize', () => {
        this.resizeCanvas('smoke-canvas', '.hero');
      });

      // 开始动画循环
      this.animateParticles();
    },

    // 初始化单个canvas的烟雾特效
    initCanvasEffect(canvasId, targetSelector) {
      // 如果还没有particles数组，创建一个
      if (!this.particles) {
        this.particles = [];
      }

      // 如果还没有canvasContexts对象，创建一个
      if (!this.canvasContexts) {
        this.canvasContexts = {};
      }

      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      // 为每个画布创建独立的上下文
      if (!this.canvasContexts[canvasId]) {
        this.canvasContexts[canvasId] = canvas.getContext('2d');
      }

      // 设置canvas大小
      this.resizeCanvas(canvasId, targetSelector);

      // 添加鼠标移动事件监听器
      const target = document.querySelector(targetSelector);
      if (target) {
        target.addEventListener('mousemove', (e) => {
          this.createParticle(e, canvasId);
        });
      }
    },
    // 调整canvas大小
    resizeCanvas(canvasId = 'smoke-canvas', targetSelector = '.hero') {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const target = document.querySelector(targetSelector);
      if (target) {
        canvas.width = target.offsetWidth;
        canvas.height = target.offsetHeight;
      }
    },
    // 创建粒子
    createParticle(e, canvasId = 'smoke-canvas') {
      const canvas = document.getElementById(canvasId);
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();

      // 创建多个粒子（增加数量）
      for (let i = 0; i < 20; i++) {
        this.particles.push({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          size: Math.random() * 20 + 10, // 增加大小
          speedX: (Math.random() - 0.5) * 6, // 增加速度
          speedY: (Math.random() - 0.5) * 6 - 2,
          opacity: Math.random() * 1 + 0.5, // 增加透明度
          life: 1,
          decay: Math.random() * 0.02 + 0.005, // 延长生命周期
          canvasId: canvasId // 记录粒子属于哪个canvas
        });
      }
    },
    // 动画循环
    animateParticles() {
      // 先清除所有canvas
      const canvases = ['smoke-canvas'];
      canvases.forEach(canvasId => {
        const canvas = document.getElementById(canvasId);
        if (canvas && this.canvasContexts && this.canvasContexts[canvasId]) {
          const ctx = this.canvasContexts[canvasId];
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });

      for (let i = this.particles.length - 1; i >= 0; i--) {
        const particle = this.particles[i];

        // 更新粒子位置
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // 更新粒子透明度
        particle.life -= particle.decay;
        particle.opacity = particle.life * 0.8;

        // 获取粒子所属的canvas上下文
        if (this.canvasContexts && this.canvasContexts[particle.canvasId]) {
          const ctx = this.canvasContexts[particle.canvasId];
          // 绘制粒子（使用灰白色）
          ctx.fillStyle = `rgba(200, 200, 200, ${particle.opacity})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
        }

        // 移除死亡的粒子
        if (particle.life <= 0) {
          this.particles.splice(i, 1);
        }
      }

      // 继续动画循环
      this.animationFrameId = requestAnimationFrame(this.animateParticles);
    }
  }
};
</script>

<style scoped>
/* 支付收银台样式 */
.payment-title {
  text-align: center;
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  margin-bottom: 2rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  animation: fadeInDown 1s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.payment-container {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.95);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.amount-section {
  margin-bottom: 30px;
}

.amount-section label {
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}

.amount-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0 15px;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
}

.amount-input-wrapper:focus-within {
  border-color: #4CAF50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.currency-symbol {
  font-size: 24px;
  font-weight: bold;
  color: #4CAF50;
  margin-right: 10px;
}

.amount-input-wrapper input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 24px;
  font-weight: bold;
  color: #333;
  padding: 15px 0;
}

.amount-input-wrapper input::placeholder {
  color: #ccc;
}

.payment-methods {
  margin-bottom: 30px;
}

.payment-methods h3 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.payment-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.payment-option:hover {
  background: #e9ecef;
}

.payment-option.active {
  background: #e8f5e8;
  border-color: #4CAF50;
}

.payment-image {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  margin-right: 12px;
  flex-shrink: 0;
  object-fit: contain;
}

.payment-option span {
  flex: 1;
  font-size: 16px;
  color: #333;
}

.radio-button {
  width: 20px;
  height: 20px;
  border: 2px solid #ccc;
  border-radius: 50%;
  position: relative;
  transition: all 0.3s ease;
}

.radio-button.checked {
  border-color: #4CAF50;
  background: #4CAF50;
}

.radio-button.checked::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background: white;
  border-radius: 50%;
}

.pay-button {
  width: 100%;
  padding: 18px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  background: #4CAF50;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.pay-button:hover:not(:disabled) {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.pay-button:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 支付调起页面样式 */
.payment-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease;
}

.payment-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.payment-modal-header {
  padding: 20px;
  background: #f8f8f8;
  border-bottom: 1px solid #e8e8e8;
  text-align: center;
}

.payment-modal-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.payment-modal-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.payment-modal-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

.payment-modal-body {
  padding: 30px 20px;
  text-align: center;
}

.payment-amount-display {
  margin-bottom: 30px;
}

.payment-amount-label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.payment-amount-value {
  font-size: 28px;
  font-weight: bold;
  color: #333;
}

.payment-info {
  margin-bottom: 30px;
}

.payment-info-text {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 10px 0;
}

.payment-info-hint {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.payment-password-section {
  margin-bottom: 30px;
}

.password-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 20px 0;
}

.password-input-container {
  margin-bottom: 15px;
}

.password-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  letter-spacing: 4px;
}

.password-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
}

.password-hint {
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
}

.payment-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.payment-action-button {
  flex: 1;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.payment-action-button.cancel {
  background: #f0f0f0;
  color: #333;
}

.payment-action-button.cancel:hover {
  background: #e6e6e6;
}

.payment-action-button.confirm {
  background: #4CAF50;
  color: white;
  border-color: #4CAF50;
}

.payment-action-button.confirm:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 银联二维码支付模态框样式 */
.unionpay-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease;
}

.unionpay-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

.unionpay-modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #e60012 0%, #ff1a3d 100%);
  text-align: center;
}

.unionpay-modal-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.unionpay-modal-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.unionpay-modal-title {
  font-size: 18px;
  font-weight: bold;
  color: white;
}

.unionpay-modal-body {
  padding: 30px 20px;
  text-align: center;
}

.qrcode-section {
  margin: 30px 0;
}

.qrcode-container {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: inline-block;
  margin-bottom: 20px;
  position: relative;
  transition: all 0.3s ease;
}

/* 移动端长按提示样式 */
.qrcode-container.mobile-long-press {
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: default;
}

.qrcode-container.mobile-long-press:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.mobile-hint {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: #e60012;
  white-space: nowrap;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.qrcode-image {
  width: 280px;
  height: 280px;
  object-fit: contain;
  display: block;
  pointer-events: auto;
  -webkit-touch-callout: default;
}

.qrcode-hint {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0;
  padding: 0 10px;
}

.unionpay-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.unionpay-action-button {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 500;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

.unionpay-action-button.cancel {
  background: #f0f0f0;
  color: #333;
}

.unionpay-action-button.cancel:hover {
  background: #e6e6e6;
}

.unionpay-action-button.download {
  background: #e60012;
  color: white;
  border-color: #e60012;
}

.unionpay-action-button.download:hover {
  background: #cc0010;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(230, 0, 18, 0.3);
}

/* 移动端保存提示样式 */
.mobile-save-hint-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  animation: fadeIn 0.3s ease;
  padding: 20px;
}

.mobile-save-hint {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 320px;
  padding: 30px 20px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

.mobile-save-hint .hint-icon {
  margin-bottom: 15px;
}

.mobile-save-hint h3 {
  font-size: 18px;
  color: #333;
  margin: 0 0 15px 0;
  font-weight: 600;
}

.mobile-save-hint .hint-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.mobile-save-hint .hint-close-btn {
  width: 100%;
  padding: 12px;
  background: #e60012;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-save-hint .hint-close-btn:hover {
  background: #cc0010;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(230, 0, 18, 0.3);
}

/* PC端保存提示样式 */
.desktop-save-hint-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  animation: fadeIn 0.3s ease;
  padding: 20px;
}

.desktop-save-hint {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 320px;
  padding: 30px 20px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

.desktop-save-hint .hint-icon {
  margin-bottom: 15px;
}

.desktop-save-hint h3 {
  font-size: 18px;
  color: #333;
  margin: 0 0 15px 0;
  font-weight: 600;
}

.desktop-save-hint .hint-text {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 20px 0;
}

.desktop-save-hint .hint-close-btn {
  width: 100%;
  padding: 12px;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.desktop-save-hint .hint-close-btn:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* 支付成功提示样式 */
.success-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease;
}

.success-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
  overflow: hidden;
  padding: 30px 20px;
  text-align: center;
}

.success-icon {
  margin-bottom: 20px;
  animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-title {
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0 0 20px 0;
}

.success-details {
  margin-bottom: 30px;
  text-align: left;
  border-top: 1px solid #f0f0f0;
  padding-top: 20px;
}

.success-detail-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
}

.success-detail-label {
  color: #666;
}

.success-detail-value {
  color: #333;
  font-weight: 500;
}

.success-modal-footer {
  margin-top: 20px;
}

.success-modal-button {
  width: 100%;
  padding: 12px;
  background: #4CAF50;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
}

.success-modal-button:hover {
  background: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

/* 响应式设计 */
@media (max-width: 480px) {
  .payment-container {
    padding: 20px;
    margin: 0 15px;
  }

  .payment-title {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
  }

  .amount-input-wrapper input {
    font-size: 20px;
  }

  .pay-button {
    padding: 15px;
    font-size: 16px;
  }

  .logo-text {
    font-size: 1.5rem;
  }

  .logo-subtext {
    font-size: 0.9rem;
  }

  .nav-actions {
    gap: 1rem;
  }

  /* 银联模态框响应式 */
  .unionpay-modal {
    width: 95%;
    max-width: none;
  }

  .qrcode-section {
    margin: 20px 0;
  }

  .qrcode-container {
    padding: 15px;
  }

  .qrcode-image {
    width: 240px;
    height: 240px;
  }

  .mobile-hint {
    font-size: 11px;
    bottom: -25px;
  }

  .unionpay-action-button {
    font-size: 13px;
    padding: 10px 12px;
  }
  
  /* 移动端保存提示样式优化 */
  .mobile-save-hint,
  .desktop-save-hint {
    max-width: 280px;
    padding: 25px 15px;
  }
  
  .mobile-save-hint h3,
  .desktop-save-hint h3 {
    font-size: 16px;
  }
  
  .mobile-save-hint .hint-text,
  .desktop-save-hint .hint-text {
    font-size: 13px;
  }
}


</style>

<style lang="scss">
/* 全局样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 线条背景效果 */
@keyframes linesMove {
  0% { background-position: 0 0; }
  100% { background-position: 100% 100%; }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* S曲线分割效果 */
@keyframes sCurvePulse {
  0%, 100% {
    clip-path: polygon(0 0, 100% 0, 100% calc(50% - 20px), 50% 50%, 0 calc(50% + 20px));
  }
  50% {
    clip-path: polygon(0 0, 100% 0, 100% calc(50% - 30px), 50% 50%, 0 calc(50% + 30px));
  }
}

html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
}

body {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.6;
  color: #fff;
  background-color: #121212;
}

.container {
  width: 100%;
  margin: 0;
  padding: 0 20px;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: #ffffff;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #666666;
  }
}

.btn {
  display: inline-block;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;

  &.primary {
    background-color: #df6f45;
    color: white;

    &:hover {
      background-color: #e88a6a;
      transform: translateY(-2px);
    }
  }

  &.secondary {
    background-color: transparent;
    color: #666666;
    border: 2px solid #666666;

    &:hover {
      background-color: #666666;
      color: white;
      transform: translateY(-2px);
    }
  }

  &.small {
    padding: 8px 16px;
    font-size: 0.9rem;
  }
}

/* 导航栏样式 */
.navbar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    background-color: transparent;
    z-index: 1000;
    transition: all 0.3s ease;
    padding: 1rem 0;

    &.scrolled {
      background-color: rgba(18, 18, 18, 0.95);
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      padding: 0.5rem 0;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }

  .container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 20px;
      max-width: 1200px;
      margin: 0 auto;
    }

  .nav-logo {
    display: flex;
    align-items: center;
  }

  .logo-link {
    display: flex;
    align-items: baseline;
    text-decoration: none;
    color: white;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .logo-text {
    font-size: 1.8rem;
    font-weight: bold;
    margin-right: 0.5rem;
    color: #4CAF50;
  }

  .logo-subtext {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
  }

  .nav-actions {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }



  .hamburger {
    display: none;
    flex-direction: column;
    cursor: pointer;
    gap: 5px;
    padding: 10px;
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;

    span {
      width: 25px;
      height: 3px;
      background-color: white;
      border-radius: 2px;
      transition: all 0.3s ease;
    }

    &.active {
      span:nth-child(1) {
        transform: rotate(45deg) translate(6px, 6px);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:nth-child(3) {
        transform: rotate(-45deg) translate(6px, -6px);
      }
    }
  }

  .language-switch {
    .el-dropdown-link {
      display: inline-block;
      padding: 8px 24px;
      background-color: white;
      color: #1e1e1e;
      text-decoration: none;
      font-weight: 600;
      font-size: 0.9rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-radius: 4px;
      transition: all 0.3s ease;

      &:hover {
        background-color: #f0f0f0;
        transform: translateY(-2px);
      }

      .el-icon {
        color: #1e1e1e;
      }
    }
  }

  .nav-links {
      display: flex;
      list-style: none;
      gap: 2rem;
      align-items: center;
      margin: 0;
      padding: 0;

      li {
        min-width: 120px; /* 增加最小宽度以容纳德语文本 */
        text-align: center;
      }

      li a {
        text-decoration: none;
        color: #fff;
        font-weight: 500;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: color 0.3s ease;

        &:hover {
          color: #666666;
        }
      }

      .logo {
        a {
          display: inline-block;
          padding: 4px 12px;
          background-color: white;
          color: #1e1e1e;
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: 1px;
          border-radius: 4px;
        }
      }
  }

  /* 语言切换按钮样式已在上方定义 */

  .hamburger {
    display: none;
    flex-direction: column;
    gap: 5px;
    cursor: pointer;

    span {
      width: 25px;
      height: 3px;
      background-color: #fff;
      transition: all 0.3s ease;
    }

    &.active {
      span:first-child {
        transform: rotate(45deg) translateY(12px);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:last-child {
        transform: rotate(-45deg) translateY(-12px);
      }
    }
  }
}

/* 英雄区域样式 */
.hero {
  height: 100vh;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  #smoke-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    pointer-events: none;
  }

  .hero-content {
    text-align: center;
    z-index: 2;
    padding: 0 20px;

    h2 {
      font-size: 5.5rem;
      margin-bottom: 1.5rem;
      color: #fff;
      font-weight: 700;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      opacity: 0;
      transform: translateY(30px);
    }

    p {
      font-size: 2rem;
      margin-bottom: 2rem;
      color: #fff;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      opacity: 0;
      transform: translateY(30px);
    }

    .cta-buttons {
      display: flex;
      gap: 1rem;
      justify-content: center;
      flex-wrap: wrap;
      opacity: 0;
      transform: translateY(30px);
    }

    // 动画类
    &.animate {
      h2 {
        animation: fadeInUp 1s ease-out 0.3s forwards;
      }

      p {
        animation: fadeInUp 1s ease-out 0.6s forwards;
      }

      .cta-buttons {
        animation: fadeInUp 1s ease-out 0.9s forwards;
      }
    }
  }

  .hero-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0; /* 确保图片显示在背景层 */

    .hero-bg {
      width: 100%;
      height: 100%;
      background-image: url('./assets/img/home-1.png');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0.8;
    }
  }

  /* 小屏幕响应式设计 */
  @media (max-width: 480px) {
    .hero-image .hero-bg {
      background-position: right 10% center; /* 让图片稍微向右移动 */
    }
  }
}

/* 功能介绍样式 - 改造后 */
.features-transform {
    position: relative;
    height: 100vh; /* 固定全屏高度 */
    background-color: #121212;
    overflow: hidden; /* 隐藏溢出内容 */
  z-index: 1;

  .features-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
  }

  /* 初始大图和文字 */
  .hero-section {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 90%;
    height: 100vh;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 1; /* 显示文本 */
    margin: 0 auto;
  }

  .hero-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('./assets/img/home-1.png');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: brightness(0.7);
    opacity: 1 !important;
    transform-origin: center;
    z-index: 1; /* 降低图片层级，确保烟雾特效显示在图片上方 */
  }

  .hero-text {
    position: relative;
    text-align: center;
    color: white;
    z-index: 4; /* 提高z-index值，确保文本显示在雾气特效上方 */
    padding: 0 80px; /* 增加左右内边距，为箭头按钮留出空间 */
    max-width: 80%; /* 限制最大宽度，避免覆盖按钮 */
    animation: fadeInUp 1.5s ease-out forwards;
  }

  /* 淡入上移动画 */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .hero-text h1 {
    font-size: 4rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  .hero-text p {
    font-size: 1.2rem;
    max-width: 600px;
    margin: 0 auto;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
    transition: opacity 0.25s ease, transform 0.25s ease;
  }

  /* 文本过渡特效 */
  .hero-text .text-transition {
    opacity: 0;
    transform: translateY(20px);
  }

  /* 图片网格 */
  .images-grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-areas:
      ". top-left top-right ."
      "left center center right"
      ". bottom-left bottom-right .";
    grid-template-columns: 1fr 300px 300px 1fr;
    grid-template-rows: 1fr 300px 1fr;
    gap: 20px;
    align-items: center;
    justify-items: center;
    opacity: 1 !important;
    z-index: 1;
  }

  /* 跑马灯样式 */
  .marquee-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 2; /* 确保跑马灯显示在最上层 */
  }

  .marquee-wrapper {
    display: flex;
    width: 100%;
    height: 100%;
    transition: transform 0.5s ease-in-out;
  }

  .marquee-slide {
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
  }

  .marquee-image {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }



  /* 响应式调整 */
  @media (max-width: 1200px) {
    /* 调整hero-text在中等屏幕上的字体大小 */
    .hero-text h1 {
      font-size: 3rem;
    }

    .hero-text p {
      font-size: 1.1rem;
    }
  }

  @media (max-width: 992px) {
    /* 调整hero-text在小屏幕上的字体大小 */
    .hero-text h1 {
      font-size: 2.5rem;
    }

    .hero-text p {
      font-size: 1rem;
    }

    /* 调整按钮大小 */
    .marquee-btn {
      width: 45px;
      height: 45px;
      font-size: 1.3rem;
    }

    /* 调整文本内边距 */
    .hero-text {
      padding: 0 60px;
    }
  }

  @media (max-width: 768px) {
    /* 调整hero-text在平板上的字体大小 */
    .hero-text h1 {
      font-size: 2rem;
    }

    .hero-text p {
      font-size: 0.9rem;
      max-width: 90%;
    }

    /* 调整按钮大小和位置 */
    .marquee-btn {
      width: 40px;
      height: 40px;
      font-size: 1.2rem;
    }

    .marquee-btn.prev {
      left: 15px;
    }

    .marquee-btn.next {
      right: 15px;
    }



    /* 调整文本内边距 */
    .hero-text {
      padding: 0 40px;
      max-width: 90%;
    }

    /* 调整hero-section宽度 */
    .hero-section {
      width: 95%;
    }
  }

  @media (max-width: 576px) {
    /* 调整hero-text在手机上的字体大小 */
    .hero-text h1 {
      font-size: 1.8rem;
      letter-spacing: 1px;
    }

    .hero-text p {
      font-size: 0.85rem;
      max-width: 100%;
    }

    /* 调整按钮大小和位置 */
    .marquee-btn {
      width: 35px;
      height: 35px;
      font-size: 1.1rem;
    }

    .marquee-btn.prev {
      left: 10px;
    }

    .marquee-btn.next {
      right: 10px;
    }



    /* 调整文本内边距 */
    .hero-text {
      padding: 0 20px;
      max-width: 100%;
    }

    /* 调整hero-section宽度 */
    .hero-section {
      width: 100%;
    }
  }

  /* 控制按钮 */
  .marquee-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 50px;
    height: 50px;
    background-color: rgba(0, 0, 0, 0.5);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 100; /* 提高z-index确保按钮在最上层 */
    outline: none;
    user-select: none;
  }

  .marquee-btn:hover {
    background-color: rgba(0, 0, 0, 0.8);
    transform: translateY(-50%) scale(1.1);
  }

  .marquee-btn.prev {
    left: 20px;
  }

  .marquee-btn.next {
    right: 20px;
  }

  /* 指示器 - 隐藏 */
  .marquee-indicators {
    display: none;
  }

  .indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .indicator.active {
    background-color: white;
    transform: scale(1.2);
  }
}

/* 产品展示样式 - 完全分离 */

/* Sopuria Products - 独立样式 */
#products {
  padding: 8rem 0;
  background-color: #181818;
  background-repeat: no-repeat;
  /* 增强的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  /* 更明显的S曲线分割 - 上半部分 */
  clip-path: polygon(0 0, 100% 0, 100% calc(100% - 80px), 50% 100%, 0 calc(100% - 80px));
  /* 线条移动动画 */
  animation: linesMove 15s linear infinite;
  /* 为下半部分准备的伪元素 */
  position: relative;
}

#products::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 80px;
  /* 稍微不同的背景色增强对比 */
  background-color: #181818;
  /* 相同的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  background-position: 0 0;
  /* 更明显的S曲线 */
  clip-path: polygon(0 0, 50% 100%, 100% 0);
  /* 添加发光边框增强S曲线 */
  border-top: 1px solid rgba(255,255,255,0.15);
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  animation: linesMove 15s linear infinite;
  z-index: 1;
}

/* Kangertech Products - 独立样式 */
#kangertech-products {
  background-color: #222222;
  background-repeat: no-repeat;
  /* 增强的线条纹理 */
  background-image:
    linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 25px 25px;
  /* 调整位置以与上部分的S曲线对接 */
  padding: 80px 0 8rem;
  /* 线条移动动画 */
  animation: linesMove 15s linear infinite;
  position: relative;
  z-index: 2;
}

  /* Sopuria Products - 4 columns - 产品内容完全独立样式 */
  #products .products-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    max-width: 100%;
    width: 100%;
    margin: 0;
    padding: 0 1rem;
    box-sizing: border-box;
  }

  /* 响应式布局 - Sopuria Products */
  @media (max-width: 1200px) {
    #products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.8rem;
    }
  }

  @media (max-width: 992px) {
    #products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.2rem;
    }
  }

  @media (max-width: 768px) {
    #products .products-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1rem;
    }

    #products .product-image {
      height: 100px;
    }

    #products .product-info {
      padding: 0.8rem;
    }

    #products .product-info h3 {
      font-size: 0.9rem;
    }

    #products .product-info p {
      font-size: 0.7rem;
      height: 30px;
    }
  }

  @media (max-width: 576px) {
    #products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 0.8rem;
    }

    #products .product-image {
      height: 90px;
    }
  }

  @media (max-width: 400px) {
    #products .products-grid {
      grid-template-columns: 1fr;
    }

    #products .product-image {
      height: 150px;
    }
  }

  /* 添加梦幻效果的关键帧动画 */
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes lightSweep {
    0% { transform: translateX(-100%) translateY(-50%) rotate(15deg); }
    100% { transform: translateX(300%) translateY(-50%) rotate(15deg); }
  }

  #products .product-item {
    background: linear-gradient(135deg, rgba(18,18,18,0.8) 0%, rgba(20,20,20,0.9) 50%, rgba(24,24,24,0.8) 100%);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 0 20px rgba(223, 111, 69, 0.15);
    transition: all 0.3s ease;
    opacity: 1;
    transform: translateY(0);
    position: relative;
    border: 1px solid rgba(223, 111, 69, 0.3);
  }

  /* 动画完成状态 */
  #products .product-item.flipped {
    opacity: 1;
    transform: translateY(0);
  }

  #products .product-item:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5), 0 0 40px rgba(223, 111, 69, 0.3);
  }

  #products .product-item::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(223, 111, 69, 0.2), transparent);
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
    z-index: 1;
  }

  #products .product-image {
    height: 250px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }

  #products .product-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: all 0.3s ease;
    filter: brightness(1.1) contrast(1.05);
  }

  #products .product-item:hover .product-bg {
    transform: scale(1.05);
    filter: brightness(1.2) contrast(1.1);
  }

  #products .product-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(223, 111, 69, 0.1) 50%, transparent 100%);
    z-index: 2;
    animation: lightSweep 4s infinite ease-in-out;
  }

  #products .product-image::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(223, 111, 69, 0.4), rgba(100, 200, 255, 0.4)) border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    z-index: 3;
  }

  #products .product-info {
    padding: 1.2rem;
    text-align: center;
    position: relative;
    z-index: 2;
  }

  #products .product-info h3 {
    font-size: 1rem;
    margin-bottom: 0.3rem;
    color: #fff;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 0 10px rgba(223, 111, 69, 0.5), 0 0 20px rgba(223, 111, 69, 0.3);
    transition: all 0.3s ease;
  }

  #products .product-item:hover .product-info h3 {
    text-shadow: 0 0 15px rgba(223, 111, 69, 0.8), 0 0 30px rgba(223, 111, 69, 0.5);
  }

  #products .product-info p {
    color: #b0b0b0;
    margin-bottom: 1rem;
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 1.5em;
    line-height: 1.5;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;
  }

  #products .product-item:hover .product-info p {
    color: #e0e0e0;
  }

  #products .product-info .btn {
    background-color: #df6f45;
    color: white;
    border: none;
    padding: 0.4rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: background-color 0.3s ease;
  }

  #products .product-info .btn:hover {
    background-color: #d65d32;
  }

  /* Kangertech Products - 5 columns - 产品内容完全独立样式 */
  #kangertech-products .products-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 2rem;
    max-width: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* 响应式布局 - Kangertech Products */
  @media (max-width: 1200px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(4, 1fr);
      gap: 1.8rem;
    }
  }

  @media (max-width: 1024px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 1.5rem;
    }
  }

  @media (max-width: 768px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1.2rem;
    }

    #kangertech-products .product-image {
      height: 180px;
    }
  }

  @media (max-width: 576px) {
    #kangertech-products .products-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
    }

    #kangertech-products .product-image {
      height: 150px;
    }
  }

  @media (max-width: 400px) {
    #kangertech-products .products-grid {
      grid-template-columns: 1fr;
    }

    #kangertech-products .product-image {
      height: 200px;
    }
  }

  #kangertech-products .product-item {
    background: linear-gradient(135deg, rgba(18,18,18,0.8) 0%, rgba(20,20,20,0.9) 50%, rgba(24,24,24,0.8) 100%);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4), 0 0 20px rgba(223, 111, 69, 0.15);
    transition: all 0.3s ease;
    opacity: 1;
    transform: translateY(0);
    position: relative;
    border: 1px solid rgba(223, 111, 69, 0.3);
  }

  /* 动画完成状态 */
  #kangertech-products .product-item.flipped {
    opacity: 1;
    transform: translateY(0);
  }

  #kangertech-products .product-item:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5), 0 0 40px rgba(223, 111, 69, 0.3);
  }

  #kangertech-products .product-item::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(45deg, transparent, rgba(223, 111, 69, 0.2), transparent);
    transform: rotate(45deg);
    animation: shimmer 3s infinite;
    z-index: 1;
  }

  #kangertech-products .product-image {
    height: 200px;
    overflow: hidden;
    position: relative;
    z-index: 1;
  }

  #kangertech-products .product-bg {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: all 0.3s ease;
    filter: brightness(1.1) contrast(1.05);
  }

  #kangertech-products .product-item:hover .product-bg {
    transform: scale(1.05);
    filter: brightness(1.2) contrast(1.1);
  }

  #kangertech-products .product-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, rgba(223, 111, 69, 0.1) 50%, transparent 100%);
    z-index: 2;
    animation: lightSweep 4s infinite ease-in-out;
  }

  #kangertech-products .product-image::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 2px solid transparent;
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(223, 111, 69, 0.4), rgba(100, 200, 255, 0.4)) border-box;
    mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    z-index: 3;
  }

  #kangertech-products .product-info {
    padding: 1.5rem;
    text-align: center;
    position: relative;
    z-index: 2;
  }

  #kangertech-products .product-info h3 {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
    color: #fff;
    font-weight: 700;
    text-shadow: 0 0 10px rgba(223, 111, 69, 0.5), 0 0 20px rgba(223, 111, 69, 0.3);
    transition: all 0.3s ease;
  }

  #kangertech-products .product-item:hover .product-info h3 {
    text-shadow: 0 0 15px rgba(223, 111, 69, 0.8), 0 0 30px rgba(223, 111, 69, 0.5);
  }

  #kangertech-products .product-info p {
    color: #b0b0b0;
    margin-bottom: 1.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    height: 1.5em;
    line-height: 1.5;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
    transition: all 0.3s ease;
  }

  #kangertech-products .product-item:hover .product-info p {
    color: #e0e0e0;
  }

  #kangertech-products .product-info .btn {
    background-color: #df6f45;
    color: white;
    border: none;
    padding: 0.5rem 1.5rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: background-color 0.3s ease;
  }

  #kangertech-products .product-info .btn:hover {
    background-color: #d65d32;
  }

/* 关于我们样式 */
.about {
  padding: 8rem 0;
  background-color: #121212;

  .about-content {
        text-align: center;
        margin: 0 20px 3rem;

        p {
          font-size: 1.1rem;
          color: #b0b0b0;
          margin-bottom: 1.5rem;
          line-height: 1.7;
        }

        /* 增加选择器优先级 */
        .fade-in-text {
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .fade-in-text.active {
          animation: fadeInUp 1s ease-out forwards;
        }

        .about-content .fade-in-text.active:first-child {
          animation-delay: 0.3s;
        }

        .about-content .fade-in-text.active:last-child {
          animation-delay: 0.6s;
        }
      }

  .about-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 2rem;
    text-align: center;

    .stat-item {
        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #df6f45;
          text-shadow: 0 0 10px rgba(253, 135, 86, 0.5);
        }

        .stat-label {
          display: block;
          margin-top: 0.5rem;
          color: #b0b0b0;
          font-weight: 500;
        }
      }
  }
}

/* 证书模块样式 */
.certificates {
  padding: 8rem 0;
  background-color: #121212;

  .section-title {
    text-align: center;
    margin-bottom: 1rem;
  }

  .section-subtitle {
    text-align: center;
    color: #b0b0b0;
    margin-bottom: 4rem;
    padding: 0 20px;
  }

  .certificates-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      margin: 0 10px;
    }

    .certificate-item {
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.3s ease;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      position: relative;
      cursor: pointer;
      opacity: 1;
      transform: translateY(0deg);
      transition: transform 0.3s ease, opacity 0.3s ease;

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);

      .certificate-info {
        opacity: 1;
      }
    }

    &.flipped {
      opacity: 1;
      transform: translateY(0deg);
    }

    .certificate-image {
      width: 100%;
      height: 250px;
      overflow: hidden;
      background-color: rgba(255, 255, 255, 0.08);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .certificate-placeholder {
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        opacity: 0.7;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 3rem;
        color: white;
      }

      .certificate-placeholder::before {
        content: "📄";
      }
    }

    .certificate-info {
      padding: 1.5rem;
      text-align: center;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: rgba(0, 0, 0, 0.8);
      opacity: 0;
      transition: opacity 0.3s ease;

      h3 {
        color: white;
        font-size: 1.2rem;
        margin-bottom: 0.5rem;
        font-weight: 600;
      }

      p {
        color: #b0b0b0;
        font-size: 0.9rem;
        margin: 0;
      }
    }
  }

  /* 移动端媒体查询 - 取消反转效果 */
  @media (max-width: 768px) {
    .certificate-item {
      opacity: 1;
      transform: translateY(0) !important;
      transition: transform 0.3s ease, opacity 0.3s ease;
    }

    .certificate-item:hover {
      transform: translateY(-10px) !important;
    }

    .certificate-item.flipped {
      opacity: 1;
      transform: translateY(0) !important;
    }
  }

  /* 小屏幕媒体查询 - 直接显示证书信息 */
  @media (max-width: 480px) {
    .certificate-item .certificate-info {
      opacity: 1; /* 直接显示证书信息，不需要鼠标悬浮 */
      position: relative; /* 改为相对定位，避免覆盖图片 */
      background: rgba(0, 0, 0, 0.6); /* 降低背景透明度，使图片更清晰 */
    }

    /* 调整证书图片高度，确保信息有足够空间显示 */
    .certificate-item .certificate-image {
      height: 200px;
    }
  }
}

/* 高清证书图片展示样式 */
.high-res-certificate {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    display: block;
    max-width: 100%;
    max-height: 80vh;
    margin: 0 auto;
    object-fit: contain;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  img[src] {
    opacity: 1;
  }
}

/* 图片加载指示器 */
.image-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(223, 111, 69, 0.3);
  border-top-color: #df6f45;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* 添加背景遮罩效果 */
.high-res-certificate::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1;
}

/* 联系我们样式 */
.contact {
  padding: 8rem 0;
  background-color: #1e1e1e;

  .contact-form {
    margin: 0 20px;

    .email-display {
      text-align: center;
      margin-bottom: 3rem;
      position: relative;
      overflow: hidden;
      display: inline-block;
      left: 50%;
      transform: translateX(-50%);
      transition: transform 0.3s ease;

      &:hover {
        transform: translateX(-50%) translateY(-10px);
      }

      .email-subtitle {
        font-size: 1rem;
        color: #666;
        margin-bottom: 1rem;
        opacity: 0.8;
      }

      .email-text {
        font-size: 7.5rem;
        font-weight: bold;
        font-family: 'Arial', 'Helvetica', sans-serif;
        color: white;
        position: relative;
        z-index: 1;
        background: linear-gradient(to right, #df6f45 50%, white 50%);
        background-size: 200% 100%;
        background-position: 100% 0;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        transition: background-position 2s ease;
        margin: 0;
      }

      /* Responsive adjustments for email text */
      @media (max-width: 1200px) {
        .email-text {
          font-size: 6rem;
        }
      }

      @media (max-width: 992px) {
        .email-text {
          font-size: 4.5rem;
        }
      }

      @media (max-width: 768px) {
        .email-text {
          font-size: 3rem;
        }
      }

      @media (max-width: 576px) {
        .email-text {
          font-size: 2rem;
        }
      }

      &:hover .email-text,
      .email-text.animate-gradient {
        background-position: 0 0;
      }
    }

    .divider {
      height: 1px;
      background-color: #333;
      margin: 3rem 0;
      width: 100%;
    }

    .contact-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 3rem;
      font-size: 0.8rem;
      color: #666;
      opacity: 0.7;
    }

    .contact-info-left,
    .contact-info-center,
    .contact-info-right {
      flex: 1;
    }

    .contact-info-left {
      text-align: left;
    }

    .contact-info-center {
      text-align: center;
    }

    .contact-info-right {
      text-align: right;
    }

    .contact-info p {
      margin: 0.3rem 0;
    }

    form {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      max-width: 600px;
      margin: 0 auto;
      width: 100%;

      .form-group {
        input,
        textarea {
          width: 100%;
          padding: 12px 16px;
          font-size: 1rem;
          border: 1px solid #333;
          border-radius: 4px;
          background-color: #121212;
          color: #fff;
          transition: border-color 0.3s ease;

          &:focus {
            outline: none;
            border-color: #666666;
          }
        }

        textarea {
          resize: vertical;
        }
      }
    }
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .navbar {
    .nav-links {
      position: fixed;
      top: 70px;
      left: -100%;
      width: 100%;
      height: calc(100vh - 70px);
      background-color: #1e1e1e;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 2rem;
      transition: left 0.3s ease;

      &.active {
        left: 0;
      }
    }

    .hamburger {
      display: flex;
    }
  }
}

/* 功能介绍区域的烟雾画布样式 - 全局生效 */
.features-transform #features-smoke-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3; /* 使用z-index:3，确保雾气特效显示在图片(z-index:2)上方 */
  pointer-events: none; /* 允许鼠标事件穿过画布 */
}

  .hero {
    height: 100vh;
    padding: 0;
    margin-top: 0;

    .hero-content {
      h2 {
        font-size: 5.5rem;
      }

      p {
        font-size: 2rem;
      }

      .cta-buttons {
        flex-direction: row;
        align-items: center;
      }
    }

    .hero-image {
      display: block;
    }
  }

  .section-title {
    font-size: 2rem;
  }

  .features {
    padding: 4rem 0;
  }

  .products {
    padding: 4rem 0;
  }

  .about {
    padding: 4rem 0;
  }

  .contact {
    padding: 4rem 0;
  }

  /* .footer {
    padding: 3rem 0 1.5rem;
  } */


@media (max-width: 480px) {
  .navbar {
    .logo h1 {
      font-size: 1.5rem;
    }
  }

  .hero {
    .hero-content {
      h2 {
        font-size: 2rem;
      }
    }
  }

  .section-title {
    font-size: 1.8rem;
  }
}

/* ProductCenter Overlay Styles */
.product-center-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 1000;
  overflow-y: auto;
  animation: fadeIn 0.5s ease-in-out;
}

.close-btn {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  font-size: 30px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  transition: background-color 0.3s ease;
}

.close-btn:hover {
  background-color: #555;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Contact form success message */
#contact .success-message {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.5);
  border-radius: 8px;
  color: #4caf50;
  text-align: center;
  font-size: 1rem;
  font-weight: 500;
  animation: fadeIn 0.5s ease-in;
}

/* WhatsApp floating button */
.whatsapp-float {
  position: fixed;
  bottom: 40px;
  right: 0px;
  width: 70px;
  height: 70px;
  background-color: #25d366;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.3), 0 0 0 4px rgba(37, 211, 102, 0.1);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  /* 响应式设计：小屏幕上调整右边距 */
  @media (max-width: 480px) {
    right: 5px;
  }
  text-decoration: none;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.whatsapp-float::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #25d366, #128c7e);
  border-radius: 50%;
  z-index: -1;
  transition: opacity 0.3s ease;
  opacity: 0;
}

.whatsapp-float:hover {
  transform: translateY(-8px) scale(1.05);
  box-shadow: 0 10px 30px rgba(37, 211, 102, 0.4), 0 0 0 8px rgba(37, 211, 102, 0.15);
}

.whatsapp-float:hover::before {
  opacity: 1;
}

.whatsapp-icon {
  width: 38px;
  height: 38px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.3s ease;
}

.whatsapp-float:hover .whatsapp-icon {
  transform: rotate(5deg) scale(1.1);
}

/* 点击效果 */
.whatsapp-float:active {
  transform: translateY(-4px) scale(0.95);
  box-shadow: 0 4px 15px rgba(37, 211, 102, 0.5);
}

.whatsapp-float:active .whatsapp-icon {
  transform: rotate(0deg) scale(0.95);
}
</style>
